import React, { useState, useCallback, useEffect, useRef } from 'react';
import AddressSearch from './AddressSearch';
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Radio,
  RadioGroup,
  Badge,
  Select,
  useToast,
} from '@chakra-ui/react';
import { getHousePrices, getPostcodeData } from '../services/housePricesService';
import { getAllEnergyData } from '../services/energyService';
import { useForm } from 'react-hook-form';
import { formatUKPostcode, isValidUKPostcode } from '../hooks/helpers/postCodeHelpers';
import config from '../config';

/**
 * Parse EPC address into paon, saon, and street components
 * @param {string} address - Full address string from EPC
 * @returns {Array} [paon, saon, street]
 */
const parseEPCAddress = (address) => {
  if (!address) return ['', '', ''];
  
  // Handle flat/apt numbers
  const flatMatch = address.match(/^(flat|apartment|apt)\.?\s*([a-z0-9]+)[,\s]+/i);
  if (flatMatch) {
    const saon = flatMatch[1] + ' ' + flatMatch[2];
    const remaining = address.substring(flatMatch[0].length);
    const numberMatch = remaining.match(/^(\d+[a-z]?)\s+(.*)/i);
    if (numberMatch) {
      return [numberMatch[1], saon, numberMatch[2]];
    }
  }
  
  // Handle standard address with number
  const numberMatch = address.match(/^(\d+[a-z]?)\s+(.*)/i);
  if (numberMatch) {
    return [numberMatch[1], '', numberMatch[2]];
  }
  
  return ['', '', address];
};
import { z } from 'zod';
import { zodResolver } from "@hookform/resolvers/zod";
import { useConfirmSelect } from "../hooks/useConfirmSelect";
import {useNavigate} from "react-router-dom";

const UK_POSTCODE_REGEX = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s\d[A-Z]{2}$/i
const schema = z.object({
  currentPostcode: z
    .string()
    .nonempty({ message: 'Postcode is required' })
    .regex(UK_POSTCODE_REGEX, {
      message:
        'Invalid UK postcode format. Must be like “CH41 8BL” (with the space)',
    }),
})

const PostcodeSearch = ({
  onAddressSelected,
  isLoadingSearchAddress,
  postcode,
  isModalOpen,
  onToggleModal,
  onUpdatePostCode,
  onUpdateLoadingState,
  onGuestSearchAction,
}) => {
  // const [isLoading, setIsLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [properties, setProperties] = useState([]);
  const [showAddressSearch, setShowAddressSearch] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  
  // Define handlePostcodeSearch first
  const handlePostcodeSearch = async (data) => {
    const postcode = data?.currentPostcode || data;
    if (!isValidUKPostcode(postcode)) {
      setError('Please enter a valid UK postcode');
      return;
    }

    // Format the postcode to ensure it has a space in the correct position
    const formattedPostcode = formatUKPostcode(postcode);
    onUpdatePostCode(formattedPostcode);
    onUpdateLoadingState(true);
    setError('');

    try {
      // First get house prices data
      const housePricesResponse = await getHousePrices(formattedPostcode);
      
      // Process house prices data
      const housePricesData = housePricesResponse?.sales || [];

      // Fetch EPC data for the postcode
      let epcProperties = [];
      try {
        const response = await fetch(
          `${config.apiUrl}/energy/epc?postcode=${encodeURIComponent(formattedPostcode)}&_t=${Date.now()}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const epcData = await response.json();

        if (epcData?.rows?.length > 0) {
          epcProperties = epcData.rows.map((epc) => {
            const epcPropertyType = (epc.PROPERTY_TYPE || epc['property-type'] || '').toLowerCase().trim();
            const epcBuiltForm = (epc.BUILT_FORM || '').toLowerCase().trim();
            
            let propertyType = 'Other';
            
            if (epcPropertyType.includes('maisonette') || 
                epcPropertyType === 'flat/maisonette' ||
                epcPropertyType === 'flat maisonette') {
              propertyType = 'Flat/Maisonette';
            } 
            else if (epcPropertyType.includes('flat') || 
                    epcPropertyType === 'flat/maisonette' ||
                    epcPropertyType === 'flat maisonette') {
              propertyType = 'Flat/Maisonette';
            }
            else if (epcPropertyType.includes('house') || 
                    epcPropertyType.includes('detached') ||
                    epcPropertyType.includes('semi') ||
                    epcPropertyType.includes('terrace')) {
              if (epcBuiltForm.includes('detached')) {
                propertyType = 'Detached';
              } else if (epcBuiltForm.includes('semi')) {
                propertyType = 'Semi-Detached';
              } else if (epcBuiltForm.includes('terrace') || 
                         epcBuiltForm.includes('mid-terrace') || 
                         epcBuiltForm.includes('end-terrace')) {
                propertyType = 'Terraced';
              } else {
                if (epcPropertyType.includes('detached')) {
                  propertyType = 'Detached';
                } else if (epcPropertyType.includes('semi')) {
                  propertyType = 'Semi-Detached';
                } else if (epcPropertyType.includes('terrace')) {
                  propertyType = 'Terraced';
                } else {
                  propertyType = 'Detached'; 
                }
              }
            }
            
            return {
              ...epc,
              address: epc.address,
              town: epc.town,
              county: epc.county,
              propertyType: propertyType,
              propertyTypeRaw: epc.PROPERTY_TYPE,
              builtForm: epc.BUILT_FORM,
              fromEPC: true
            };
          });
        }
      } catch (error) {
        console.error('Error fetching EPC data:', error);
      }

      if (!housePricesData.length && !epcProperties.length) {
        setError('No properties found for this postcode');
        onUpdateLoadingState(false);
        return;
      }

      const normalizeAddress = (address) => {
        const parts = [];
        if (address.paon) parts.push(address.paon);
        if (address.street) parts.push(address.street);
        return parts.join(' ').toUpperCase().replace(/\s+/g, ' ').trim();
      };

      const processedHousePrices = housePricesData
        .filter(prop => prop.propertyType && prop.propertyType !== 'Unknown')
        .map(prop => ({
          ...prop,
          address: {
            paon: prop.address?.paon?.toUpperCase() || '',
            street: prop.address?.street?.toUpperCase() || '',
            town: prop.address?.town?.toUpperCase() || '',
            county: prop.address?.county?.toUpperCase() || ''
          }
        }));

      const seenAddresses = new Set();
      const mergedProperties = [];

      processedHousePrices.forEach(prop => {
        const addressKey = normalizeAddress(prop.address);
        if (addressKey && !seenAddresses.has(addressKey)) {
          seenAddresses.add(addressKey);
          mergedProperties.push({
            ...prop,
            propertyType: prop.propertyType || 'Unknown',
            fromEPC: false
          });
        }
      });

      epcProperties.forEach(epcProp => {
        const addressKey = normalizeAddress(epcProp.address);
        if (addressKey && !seenAddresses.has(addressKey)) {
          seenAddresses.add(addressKey);
          mergedProperties.push(epcProp);
        }
      });

      setProperties(mergedProperties);
      
      if (mergedProperties.length > 0) {
        onToggleModal(true);
      } else {
        setShowAddressSearch(true);
      }
    } catch (error) {
      console.error('Error searching properties:', error);
      setError('Failed to search properties. Please try again.');
    } finally {
      onUpdateLoadingState(false);
    }
  };
  
  const [localPostcode, setLocalPostcode] = useState(postcode || '');
  const [error, setError] = useState('');
  
  // Update local state when prop changes
  useEffect(() => {
    setLocalPostcode(postcode || '');
  }, [postcode]);

  const validatePostcode = (postcode) => {
    if (!postcode.trim()) {
      return 'Postcode is required';
    }
    if (!isValidUKPostcode(postcode)) {
      return 'Please enter a valid UK postcode';
    }
    return '';
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalPostcode(value);
    // Clear error when user types
    if (error) setError('');
  };

  // Form submit handler
  const onSubmit = (e) => {
    e?.preventDefault?.();
    const validationError = validatePostcode(localPostcode);
    if (validationError) {
      setError(validationError);
      return;
    }
    handlePostcodeSearch({ currentPostcode: localPostcode });
  };

  const { mutateAsync } = useConfirmSelect();

  // Group properties by house/flat number for selection
  const groupedProperties = useCallback(() => {
    if (!properties || properties.length === 0) return [];
    const propertyMap = new Map();
    properties.forEach((property) => {
      const paon = property.address?.paon || 'Unknown';
      const saon = property.address?.saon || '';
      const addressLine = property.address?.street || '';
      // Use a more robust key if multiple properties can exist at the exact same paon/saon/street (e.g. different transaction dates)
      // For now, assuming paon/saon/street is unique enough for grouping display options.
      const key = `${paon}-${saon}-${addressLine}`;
      if (!propertyMap.has(key)) {
        propertyMap.set(key, property);
      }
    });

    const sortedProperties = Array.from(propertyMap.values()).sort((a, b) => {
      const paonA = String(a.address?.paon || '');
      const paonB = String(b.address?.paon || '');
      const saonA = String(a.address?.saon || '');
      const saonB = String(b.address?.saon || '');

      // Helper to extract initial numeric part of PAON
      const getNumericPart = (paonStr) => {
        const match = paonStr.match(/^(\d+)/);
        return match ? parseInt(match[1], 10) : null;
      };

      const numA = getNumericPart(paonA);
      const numB = getNumericPart(paonB);

      // Primary sort: by numeric part of PAON if both have it
      if (numA !== null && numB !== null) {
        if (numA !== numB) return numA - numB;
        // If numeric parts are the same, compare full PAON string to handle cases like 1A vs 1B
        const paonFullCompare = paonA.localeCompare(paonB);
        if (paonFullCompare !== 0) return paonFullCompare;
      } else if (numA !== null) {
        return -1; // Numeric PAONs first
      } else if (numB !== null) {
        return 1;  // Numeric PAONs first
      } else {
        // Both PAONs are non-numeric, sort them alphabetically
        const paonAlphaCompare = paonA.localeCompare(paonB);
        if (paonAlphaCompare !== 0) return paonAlphaCompare;
      }

      // Secondary sort: by SAON (flat number), alphabetically
      return saonA.localeCompare(saonB);
    });

    return sortedProperties.map((prop) => ({
      id: `${prop.address?.paon || 'unknown'}-${prop.address?.saon || 'main'}-${prop.address?.street || 'unknown'}`,
      paon: prop.address?.paon || '',
      saon: prop.address?.saon || '',
      street: prop.address?.street || '',
      town: prop.address?.town || '',
      county: prop.address?.county || '',
      postcode: prop.postcode || postcode,
      propertyType: prop.propertyType || '',
      price: prop.price || 0,
      date: prop.date || '',
    }));
  }, [properties, postcode]);

  /**
   * Check if selected property has unknown property type or comes from getaddress.io
   */
  const hasUnknownPropertyType = () => {
    const selected = groupedProperties().find((prop) => prop.id === selectedProperty);
    
    // Add debugging to see what we're working with
    console.log('Selected property:', selected);
    
    if (!selected) return false;
    
    // Only check if property type is actually missing, unknown, or empty
    // Don't assume properties without price data need property type selection
    return !selected.propertyType || 
           selected.propertyType === 'unknown' || 
           selected.propertyType === '';
  };

  /**
   * Handle property selection
   */
  const handlePropertySelect = async () => {
    console.log('Selected property ID:', selectedProperty);
    const selected = groupedProperties().find((prop) => prop.id === selectedProperty);
    console.log('Selected property data:', selected);
    console.log('Property type needs selection:', hasUnknownPropertyType());
    console.log('Current selected property type:', selectedPropertyType);

    if (selected) {
      // If property type is unknown and user hasn't selected a type yet, don't proceed
      if (hasUnknownPropertyType() && !selectedPropertyType) {
        toast({
          title: 'Property Type Required',
          description: 'Please select a property type to continue',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // If user selected a property type for an unknown property type, use that
      if (hasUnknownPropertyType() && selectedPropertyType) {
        selected.propertyType = selectedPropertyType;
      }

      try {
        // Fetch postcode data from postcodes.io API
        const postcodeData = await getPostcodeData(selected.postcode);
        console.log('postcodeData', postcodeData);

        // Add latitude, longitude, and region to the selected property if available
        if (postcodeData) {
          selected.latitude = postcodeData.latitude;
          selected.longitude = postcodeData.longitude;
          selected.region = postcodeData.region;
        }

      } catch (err) {
      }

      try {
        await mutateAsync().catch(() => {});
        toast({
          title: 'Address Selected',
          description: 'Property details loaded successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onAddressSelected(selected);
      } catch (error) {
        console.error('Error selecting address:', error);
        onAddressSelected(selected);
      } finally {
        onToggleModal(false);
        onUpdatePostCode('');
        setSelectedProperty('');
        setSelectedPropertyType('');
        onUpdateLoadingState(false);
      }
    }
  };

  // Format the address for display
  const formatAddress = (address) => {
    const parts = [];

    // Handle flat/unit number if present
    if (address.saon) {
      parts.push(address.saon);
    }
    
    // Add house number/name if present
    if (address.paon) {
      parts.push(address.paon);
    }
    
    // Add street name
    if (address.street) {
      // Clean up any double commas in the street name
      const cleanStreet = address.street.replace(/,\s*,/g, ',').replace(/,$/, '');
      parts.push(cleanStreet);
    }
    
    // Add posttown if available
    if (address.town) {
      parts.push(address.town);
    }
    
    // Add postcode if available
    if (address.postcode) {
      parts.push(address.postcode);
    }
    
    // Join all non-empty parts with comma and space
    return parts.filter(Boolean).join(', ');
  };

  // State for manual entry
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualHouseNumber, setManualHouseNumber] = useState('');
  const [manualFlatNumber, setManualFlatNumber] = useState('');
  const [manualPropertyType, setManualPropertyType] = useState('');

  // Handle address selection from address search
  const handleAddressSelect = async (addressData) => {
    console.log('Address selected from Google Maps:', addressData);
    
    // Set loading state
    onUpdateLoadingState(true);
    
    // Extract address components for matching
    const extractedAddress = {
      houseNumber: '',
      flatNumber: '',
      street: '',
      postcode: addressData.postcode,
      town: addressData.address?.town || '',
      county: addressData.address?.county || ''
    };

    // Parse the address line1 to extract house number and flat number
    const addressLine1 = addressData.address.line1 || '';
    
    // More comprehensive address parsing
    // Try different patterns to extract flat/unit and house numbers
    // 1. Flat/Unit X, 123 Street
    let flatMatch = addressLine1.match(/^(flat|unit|apartment|apt|flt|fl)\s*[#]?\s*(\d+[a-z]?)\b[,\s]*(.+)/i);
    
    if (!flatMatch) {
      // 2. 123A, Flat X, Street
      flatMatch = addressLine1.match(/^(\d+[a-z]?)\s*[,\s]+(flat|unit|apartment|apt|flt|fl)\s*[#]?\s*(\d+[a-z]?)\b[,\s]*(.+)/i);
      if (flatMatch) {
        extractedAddress.houseNumber = flatMatch[1];
        extractedAddress.flatNumber = flatMatch[3];
        extractedAddress.street = flatMatch[4];
      } else {
        // 3. Just house number and street
        const houseMatch = addressLine1.match(/^(\d+[a-z]?[\s-]?[a-z]?)(?:\s*[,\s]*\s*)([^,]+)/i) || 
                         addressLine1.match(/^(\d+[a-z]?[\s-]?[a-z]?)\s+(.+)/i);
        
        if (houseMatch) {
          extractedAddress.houseNumber = houseMatch[1].trim();
          extractedAddress.street = houseMatch[2].trim();
        } else {
          // 4. Just street name (no number)
          extractedAddress.street = addressLine1;
        }
      }
    } else {
      extractedAddress.flatNumber = flatMatch[2];
      const remainingAddress = flatMatch[3].trim();
      
      // Try to extract house number from remaining address
      const houseMatch = remainingAddress.match(/^(\d+[a-z]?[\s-]?[a-z]?)\s*(.*)/i);
      if (houseMatch) {
        extractedAddress.houseNumber = houseMatch[1];
        extractedAddress.street = houseMatch[2].trim() || extractedAddress.street;
      } else {
        extractedAddress.street = remainingAddress;
      }
    }

    console.log('Extracted address components:', extractedAddress);

    // Update the postcode in the parent component
    if (addressData.postcode) {
      onUpdatePostCode(addressData.postcode);
    }

    // Hide the address search
    setShowAddressSearch(false);

    // Now trigger the postcode search to get all available properties
    try {
      // Store a promise that resolves when properties are loaded
      const propertiesLoaded = new Promise((resolve) => {
        const checkProperties = () => {
          if (properties && properties.length > 0) {
            resolve(properties);
          } else {
            setTimeout(checkProperties, 100);
          }
        };
        checkProperties();
      });
      
      // Start the postcode search
      await handlePostcodeSearch(addressData.postcode);
      
      // Wait for properties to be loaded
      await propertiesLoaded;
      
      console.log('Properties loaded, attempting to match address...');
      
      // Try to auto-match the property
      await autoMatchProperty(extractedAddress);
      
    } catch (error) {
      console.error('Error searching for properties:', error);
      toast({
        title: 'Error',
        description: 'Failed to search for properties in this postcode',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Helper function to automatically match and select a property
  const autoMatchProperty = async (extractedAddress) => {
    if (!properties || properties.length === 0) {
      console.log('No properties available for matching');
      return;
    }

    console.log('Attempting to auto-match property:', extractedAddress);
    console.log('Available properties:', properties);

    // Normalize the extracted address for comparison
    const normalizeForComparison = (str) => {
      if (!str) return '';
      return str
        .toString()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '') // Remove all non-alphanumeric characters
        .trim();
    };

    // Try to find a matching property
    let bestMatch = null;
    let exactMatches = [];

    properties.forEach(property => {
      const propPaon = property.address?.paon || '';
      const propSaon = property.address?.saon || '';
      
      // Normalize house numbers for comparison
      const normalizedPropPaon = normalizeForComparison(propPaon);
      const normalizedExtractedHouse = normalizeForComparison(extractedAddress.houseNumber);
      
      // Check for house number match (more flexible matching)
      const houseNumberMatch = (
        normalizedPropPaon === normalizedExtractedHouse ||
        normalizedPropPaon.replace(/^0+/, '') === normalizedExtractedHouse.replace(/^0+/, '') ||
        normalizedPropPaon.replace(/[^0-9]/g, '') === normalizedExtractedHouse.replace(/[^0-9]/g, '')
      );
      
      // Check for flat number match if we have one
      let flatNumberMatch = true; // Default to true if no flat number to match
      if (extractedAddress.flatNumber) {
        const normalizedPropSaon = normalizeForComparison(propSaon);
        const normalizedExtractedFlat = normalizeForComparison(extractedAddress.flatNumber);
        
        flatNumberMatch = (
          normalizedPropSaon.includes(normalizedExtractedFlat) ||
          normalizedExtractedFlat.includes(normalizedPropSaon) ||
          normalizedPropSaon.replace(/[^0-9]/g, '') === normalizedExtractedFlat.replace(/[^0-9]/g, '')
        );
      }
      
      // If we have a house number match and (flat number matches or no flat number to match)
      if (houseNumberMatch && flatNumberMatch) {
        exactMatches.push(property);
      }
    });

    console.log('Exact matches found:', exactMatches);

    if (exactMatches.length === 1) {
      // Perfect match found - auto-select it
      bestMatch = exactMatches[0];
      console.log('Auto-selecting property:', bestMatch);
      
      // Set the selected property and wait for state update
      setSelectedProperty(bestMatch.id);
      
      // Show success message
      toast({
        title: 'Address Matched',
        description: `Automatically selected: ${bestMatch.address?.paon} ${bestMatch.address?.street}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Use a longer timeout to ensure state is updated and then trigger property loading
      setTimeout(async () => {
        try {
          console.log('Triggering property selection for auto-matched property');
          
          // Create a deep copy of the best match to avoid modifying the original
          const selected = JSON.parse(JSON.stringify(bestMatch));
          
          // Ensure the address object exists and has all required fields
          selected.address = selected.address || {};
          
          // Merge address components from the extracted address with the matched property
          // This ensures we have the most complete address information possible
          selected.address = {
            // Start with the matched property's address (if any)
            ...selected.address,
            // Override with any extracted components that exist
            ...(extractedAddress.houseNumber && { paon: extractedAddress.houseNumber }),
            ...(extractedAddress.flatNumber && { saon: extractedAddress.flatNumber }),
            ...(extractedAddress.street && { street: extractedAddress.street }),
            // Ensure we have at least the town and postcode from the extracted address
            ...(extractedAddress.town && { town: extractedAddress.town }),
            ...(extractedAddress.county && { county: extractedAddress.county })
          };
          
          // Ensure postcode is set at the root level
          if (extractedAddress.postcode) {
            selected.postcode = extractedAddress.postcode;
          }
          
          // Ensure we have a complete address object
          selected.address = {
            // Default empty values
            line1: '',
            line2: '',
            line3: '',
            town: '',
            county: '',
            postcode: selected.postcode || '',
            country: 'United Kingdom',
            // Override with any existing values
            ...selected.address,
            // Ensure we have the most specific values
            ...(selected.address.paon && { paon: selected.address.paon }),
            ...(selected.address.saon && { saon: selected.address.saon }),
            ...(selected.address.street && { street: selected.address.street })
          };
          
          // Create a formatted display address
          const addressParts = [];
          if (selected.address.saon) addressParts.push(selected.address.saon);
          if (selected.address.paon) addressParts.push(selected.address.paon);
          if (selected.address.street) addressParts.push(selected.address.street);
          if (selected.address.town) addressParts.push(selected.address.town);
          if (selected.address.county) addressParts.push(selected.address.county);
          if (selected.postcode) addressParts.push(selected.postcode);
          
          selected.formattedAddress = addressParts.join(', ');
          
          console.log('Merged address data:', {
            original: bestMatch.address,
            extracted: extractedAddress,
            merged: selected.address,
            formatted: selected.formattedAddress
          });
          
          // Check if property type is unknown and handle it
          if (hasUnknownPropertyType() && !selectedPropertyType) {
            // For auto-matched properties, we'll skip the property type selection
            // and let the user handle it later if needed
            console.log('Property type unknown for auto-matched property, proceeding anyway');
          }

          // If user selected a property type for an unknown property type, use that
          if (hasUnknownPropertyType() && selectedPropertyType) {
            selected.propertyType = selectedPropertyType;
          }

          try {
            // Fetch postcode data from postcodes.io API
            const postcodeData = await getPostcodeData(selected.postcode);
            console.log('postcodeData', postcodeData);

            // Add latitude, longitude, and region to the selected property if available
            if (postcodeData) {
              selected.latitude = postcodeData.latitude;
              selected.longitude = postcodeData.longitude;
              selected.region = postcodeData.region;
            }
          } catch (err) {
            console.error('Error fetching postcode data:', err);
          }

          try {
            await mutateAsync().catch(() => {});
            toast({
              title: 'Property Loaded',
              description: 'Property details loaded successfully',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });

            const formattedAddress = formatAddress(selected.address);
            const propertyWithFormattedAddress = {
              ...selected,
              formattedAddress: formattedAddress,
            };

            onAddressSelected(propertyWithFormattedAddress);
          } catch (error) {
            console.error('Error loading property:', error);
            const formattedAddress = formatAddress(selected.address);
            onAddressSelected({
              ...selected,
              formattedAddress,
            });
          } finally {
            // Clean up state
            onUpdatePostCode('');
            setSelectedProperty('');
            setSelectedPropertyType('');
            onUpdateLoadingState(false);
            // Ensure modal is closed after all operations
            onToggleModal(false);
            
            // Format the address before passing to onAddressSelected
            const formatAddress = (address) => {
              if (!address) return '';
              const parts = [];
              if (address.saon) parts.push(address.saon);
              if (address.paon) parts.push(address.paon);
              if (address.street) parts.push(address.street);
              if (address.town) parts.push(address.town);
              if (address.county) parts.push(address.county);
              if (address.postcode) parts.push(address.postcode);
              return parts.join(', ');
            };
            
            // Ensure we have all necessary data before calling onAddressSelected
            const propertyWithFormattedAddress = {
              ...selected,
              formattedAddress: selected.formattedAddress || formatAddress(selected.address)
            };
            
            console.log('Sending property to onAddressSelected:', propertyWithFormattedAddress);
            onAddressSelected(propertyWithFormattedAddress);
          }
        } catch (error) {
          console.error('Error in auto-property selection:', error);
          toast({
            title: 'Error',
            description: 'Failed to load property details',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      }, 1000);
      
    } else if (exactMatches.length > 1) {
      // Multiple matches - pre-select the first one but show the modal
      console.log('Multiple matches found, showing modal with pre-selection');
      setSelectedProperty(exactMatches[0].id);
      
      toast({
        title: 'Multiple Matches Found',
        description: 'Please select the correct property from the list',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } else {
      // No exact match - show modal for manual selection
      console.log('No exact match found, showing modal for manual selection');
      
      toast({
        title: 'Property Not Found',
        description: 'Please select your property from the available options',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle manual property entry
  const handleManualEntry = async () => {
    if (!manualHouseNumber) {
      toast({
        title: 'House number required',
        description: 'Please enter a house number to continue',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Create a property object from manual entry
      const manualProperty = {
        id: `manual-${manualHouseNumber}-${manualFlatNumber || 'main'}`,
        paon: manualHouseNumber,
        saon: manualFlatNumber || '',
        street: properties[0]?.address?.street || '',
        town: properties[0]?.address?.town || '',
        county: properties[0]?.address?.county || '',
        postcode: postcode,
        propertyType: manualPropertyType || 'unknown',
        price: 0,
        date: '',
      };

      // Fetch postcode data from postcodes.io API
      const postcodeData = await getPostcodeData(postcode);

      // Add latitude, longitude, and region to the manual property if available
      if (postcodeData) {
        manualProperty.latitude = postcodeData.latitude;
        manualProperty.longitude = postcodeData.longitude;
        manualProperty.region = postcodeData.region;
      }

      onAddressSelected(manualProperty);
      onToggleModal(false);
      onUpdatePostCode('');
      setManualHouseNumber('');
      setManualFlatNumber('');
      setManualPropertyType('');
      setShowManualEntry(false);

      toast({
        title: 'Address Added',
        description: 'Your manually entered address has been added',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error fetching postcode data:', error);
    } finally {
      onUpdateLoadingState(false);
    }
  };

  return (
    <Box>
      <VStack as="form" onSubmit={onSubmit} spacing={4} align="stretch">
        {!showAddressSearch ? (
          <>
            <FormControl isInvalid={!!error}>
              <FormLabel>Enter Postcode</FormLabel>
              <InputGroup>
                <Input
                  placeholder="e.g. SW1A 1AA"
                  name="currentPostcode"
                  value={localPostcode}
                  onChange={handleInputChange}
                  isDisabled={isLoadingSearchAddress}
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    type="submit"
                    isLoading={isLoadingSearchAddress}
                    isDisabled={isLoadingSearchAddress}
                  >
                    Search
                  </Button>
                </InputRightElement>
              </InputGroup>
              {error && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {error}
                </Text>
              )}
            </FormControl>
            <Button
              variant="link"
              colorScheme="orange"
              color="orange.500"
              onClick={() => setShowAddressSearch(true)}
              _hover={{ textDecoration: 'underline' }}
            >
              Or search by address
            </Button>
          </>
        ) : (
          <>
            <FormLabel>Search for an address{postcode ? ` in ${postcode}` : ''}</FormLabel>
            <AddressSearch
              onSelect={handleAddressSelect}
              onClear={() => setShowAddressSearch(false)}
              postcode={postcode}
            />
          </>
        )}
      </VStack>

      {/* Property selection modal */}
      <Modal isOpen={isModalOpen} onClose={() => onToggleModal(false)} size='lg'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Your Property</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              We found {groupedProperties().length} properties in {postcode}. Please select your
              address:
            </Text>
          </ModalBody>
          <ModalBody maxH="50dvh" overflowY="auto">
            {!showManualEntry ? (
              <>
                <RadioGroup value={selectedProperty} onChange={setSelectedProperty}>
                  <VStack align='start' spacing={3}>
                    {groupedProperties().map((property) => (
                      <Box key={property.id} w='100%' p={2} borderWidth='1px' borderRadius='md'>
                        <Radio value={property.id} w='100%'>
                          <HStack justifyContent='space-between' w='100%'>
                            <Text fontWeight='bold'>{formatAddress(property)}</Text>
                            {property.propertyType && (
                              <Badge
                                colorScheme={
                                  property.propertyType.includes('flat')
                                    ? 'purple'
                                    : property.propertyType.includes('terraced')
                                    ? 'blue'
                                    : property.propertyType.includes('detached')
                                    ? 'green'
                                    : property.propertyType.includes('semi')
                                    ? 'teal'
                                    : 'gray'
                                }
                              >
                                {property.propertyType.charAt(0).toUpperCase() +
                                  property.propertyType.slice(1)}
                              </Badge>
                            )}
                          </HStack>
                        </Radio>
                      </Box>
                    ))}
                  </VStack>
                </RadioGroup>
              </>
            ) : (
              <Box p={4} borderWidth='1px' borderRadius='md'>
                <Text fontWeight='medium' mb={3}>
                  Enter your property details
                </Text>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>House Number</FormLabel>
                    <Input
                      placeholder='e.g. 42'
                      value={manualHouseNumber}
                      onChange={(e) => setManualHouseNumber(e.target.value)}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Flat/Unit Number (if applicable)</FormLabel>
                    <Input
                      placeholder='e.g. Flat 3 or Apartment B'
                      value={manualFlatNumber}
                      onChange={(e) => setManualFlatNumber(e.target.value)}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Property Type</FormLabel>
                    <Select
                      placeholder='Select property type'
                      value={manualPropertyType}
                      onChange={(e) => setManualPropertyType(e.target.value)}
                    >
                      <option value='detached'>Detached</option>
                      <option value='semi-detached'>Semi-detached</option>
                      <option value='terraced'>Terraced</option>
                      <option value='flat/maisonette'>Flat/Maisonette</option>
                      <option value='other'>Other</option>
                    </Select>
                  </FormControl>

                  <HStack spacing={4} width='100%'>
                    <Button colorScheme='blue' onClick={handleManualEntry}>
                      Add Address
                    </Button>
                    <Button variant='outline' onClick={() => setShowManualEntry(false)}>
                      Back to List
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            )}
          </ModalBody>

          {!showManualEntry && (
            <ModalBody>
              <Box width={"100%"} mt={6} p={3} borderWidth='1px' borderRadius='md' bg='gray.50'>
                <Text fontWeight='medium' mb={2}>
                  Can't find your property?
                </Text>
                <Button size='sm' onClick={() => setShowManualEntry(true)}>
                  Enter address manually
                </Button>
              </Box>
            </ModalBody>
          )}
          {/* Property type selection when unknown */}
          {!showManualEntry && selectedProperty && hasUnknownPropertyType() && (
            <ModalBody>
              <Box width={'100%'} mt={3} p={3} borderWidth='1px' borderRadius='md' bg='blue.50'>
                <Text fontWeight='medium' mb={2}>
                  Please select your property type:
                </Text>
                <FormControl isRequired>
                  <Select
                    placeholder='Select property type'
                    value={selectedPropertyType}
                    onChange={(e) => setSelectedPropertyType(e.target.value)}
                    mb={2}
                  >
                    <option value='detached'>Detached</option>
                    <option value='semi-detached'>Semi-detached</option>
                    <option value='terraced'>Terraced</option>
                    <option value='flat/maisonette'>Flat/Maisonette</option>
                    <option value='other'>Other</option>
                  </Select>
                </FormControl>
              </Box>
            </ModalBody>
          )}

          <ModalFooter>
            {!showManualEntry && (
              <>
                <Button
                  colorScheme='blue'
                  mr={3}
                  onClick={handlePropertySelect}
                  isDisabled={!selectedProperty}
                >
                  Confirm Selection
                </Button>
                <Button variant='ghost' onClick={() => onToggleModal(false)}>
                  Cancel
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PostcodeSearch;
